import { DirectReportDto, ProductVisibility } from "@coaching-culture/types";
import {
  Button,
  DotMenu,
  formatDate,
  Label,
  typeSize,
  UserProfileImage,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { useGoalTerminology } from "auth/OrgProvider";
import { useNudge } from "components/NudgeButton";
import { useProductVisibility } from "hooks/useProductVisibility";
import { useDeleteRelationship } from "queries/team";
import React from "react";
import { IconType } from "react-icons";
import {
  FaBullseye,
  FaClipboard,
  FaCommentDots,
  FaEnvelope,
  FaEye,
  FaRegChartBar,
  FaSeedling,
  FaTrash,
} from "react-icons/fa";
import { useHistory } from "react-router";
import styled from "styled-components";
import {
  MetricStatus,
  metricStatusLabels,
  useTeamRagThresholds,
} from "./metrics";
import {
  areaDetails,
  cardStatuses,
  firstName,
  nextStepFor,
  TeamDrawer,
} from "./nextStep";
import { Strip, StripTag, StripText, StripTitle } from "./NextStepStrip";

const Card = styled.article<{ clickable?: boolean; pending?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.colors.surface};
  border: 1px ${(p) => (p.pending ? "dashed" : "solid")}
    ${(p) => (p.pending ? p.theme.colors.borderStrong : p.theme.colors.border)};
  border-radius: 12px;
  cursor: ${(p) => (p.clickable ? "pointer" : "default")};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${(p) =>
      p.clickable ? p.theme.colors.borderStrong : undefined};
  }
`;

const CardHeader = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 18px 18px 14px;
`;

const NameBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NameButton = styled.button`
  all: unset;
  font-size: ${typeSize("title")};
  font-weight: 600;
  color: ${(p) => p.theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Name = styled.span`
  font-size: ${typeSize("title")};
  font-weight: 600;
  color: ${(p) => p.theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.span`
  font-size: ${typeSize("small")};
  color: ${(p) => p.theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Same width as the dot menu, so names line up on cards without one. */
const MenuSpacer = styled.span`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
`;

const CardStrip = styled(Strip)`
  margin: 0 18px;
`;

const Rows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px 18px 0;
  display: flex;
  flex-direction: column;
`;

const Row = styled.li<{ muted?: boolean }>`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey5};

  &:last-child {
    border-bottom: 0;
  }

  & > svg {
    width: 16px;
    height: 16px;
    fill: ${(p) => (p.muted ? p.theme.colors.grey3 : p.theme.colors.grey)};
  }
`;

const RowText = styled.div<{ muted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  font-size: ${typeSize("small")};

  & > strong {
    font-weight: 600;
    color: ${(p) => (p.muted ? p.theme.colors.grey2 : p.theme.colors.black)};
  }

  & > span {
    color: ${(p) => (p.muted ? p.theme.colors.grey2 : p.theme.colors.textMuted)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: auto;
  padding: 10px 12px 12px;
  border-top: 1px solid ${(p) => p.theme.colors.grey5};

  & > button {
    flex: 1;
  }
`;

type AreaRowProps = {
  icon: IconType;
  title: string;
  detail: string;
  status?: MetricStatus;
  muted?: boolean;
};

const AreaRow = ({ icon: Icon, title, detail, status, muted }: AreaRowProps) => (
  <Row muted={muted}>
    <Icon aria-hidden />
    <RowText muted={muted}>
      <strong>{title}</strong>
      <span title={detail}>{detail}</span>
    </RowText>
    {status != null && (
      <Label size="small" color={status}>
        {metricStatusLabels[status]}
      </Label>
    )}
  </Row>
);

/** Stops a click inside the card from also opening the modal. */
const stop =
  (fn: () => void) =>
  (ev: React.MouseEvent): void => {
    ev.stopPropagation();
    fn();
  };

export type TeamViewCardProps = {
  user: DirectReportDto;
  /** Opens the team member modal, optionally straight on a drawer. */
  onSelect: (drawer?: TeamDrawer) => void;
  onViewPrivateNotes: () => void;
  onViewDevelopmentNotes: () => void;
};

export const TeamViewCard = (props: TeamViewCardProps) => {
  const { user } = props;
  const isConfirmed =
    user.relationship.toAccepted != null &&
    user.relationship.fromAccepted != null;

  return isConfirmed ? (
    <ConfirmedCard {...props} />
  ) : (
    <UnconfirmedCard user={user} />
  );
};

const ConfirmedCard = ({
  user,
  onSelect,
  onViewPrivateNotes,
  onViewDevelopmentNotes,
}: TeamViewCardProps) => {
  const [me] = useUser();
  const terms = useGoalTerminology();
  const delRel = useDeleteRelationship();
  const history = useHistory();
  const hasMeasures =
    useProductVisibility("measures") === ProductVisibility.Enabled;
  const thresholds = useTeamRagThresholds();

  const statuses = cardStatuses(user, thresholds, hasMeasures);
  const step = nextStepFor(user, me.id, statuses, terms);

  const onItemClick = (_idx: number, cmd: string) => {
    if (cmd === "del") {
      if (
        window.confirm("Are you sure you want to remove this relationship?")
      ) {
        delRel.mutateAsync(user.relationship.id);
      }
    } else if (cmd === "profile") {
      history.push(`/solutions/my-team/${user.id}`);
    } else if (cmd === "notes") {
      onViewPrivateNotes();
    } else if (cmd === "dev-notes") {
      onViewDevelopmentNotes();
    }
  };

  const runStep = () => {
    if (step.href) {
      history.push(step.href);
    } else {
      onSelect(step.drawer);
    }
  };

  const details = areaDetails(user, me.id, terms);

  return (
    <Card clickable onClick={() => onSelect()}>
      <CardHeader>
        <UserProfileImage
          profileImage={user.profileImage}
          name={user.name}
          size={48}
        />
        <NameBlock>
          <NameButton type="button" onClick={stop(() => onSelect())}>
            {user.name}
          </NameButton>
          <Subtitle>{user.jobTitle ?? user.email}</Subtitle>
        </NameBlock>
        <DotMenu
          items={[
            { icon: FaEye, label: "View Profile", cmd: "profile" },
            { icon: FaClipboard, label: "View Private Notes", cmd: "notes" },
            {
              icon: FaSeedling,
              label: "View Development Notes",
              cmd: "dev-notes",
            },
            {
              icon: FaTrash,
              color: "danger",
              label: "Delete Relationship",
              cmd: "del",
            },
          ]}
          onItemClick={onItemClick}
        />
      </CardHeader>

      <CardStrip tone={step.tone}>
        <StripText>
          <StripTag tone={step.tone}>{step.tag}</StripTag>
          <StripTitle title={step.title}>{step.title}</StripTitle>
        </StripText>
        <Button
          variant={step.tone === "calm" ? "secondary" : "primary"}
          onClick={stop(runStep)}
        >
          {step.cta}
        </Button>
      </CardStrip>

      <Rows>
        <AreaRow
          icon={FaBullseye}
          title={terms.goal.asPluralTitle()}
          detail={details.goals}
          status={statuses.goals}
        />
        <AreaRow
          icon={FaCommentDots}
          title="Conversations"
          detail={details.conversations}
          status={statuses.conversations}
        />
        <AreaRow
          icon={FaEnvelope}
          title="Feedback"
          detail={details.feedback}
          status={statuses.feedback}
        />
        {statuses.measures != null && (
          <AreaRow
            icon={FaRegChartBar}
            title="Measures"
            detail={details.measures}
            status={statuses.measures}
          />
        )}
      </Rows>

      <Footer>
        <Button variant="ghost" onClick={stop(() => onSelect("conversation"))}>
          Schedule
        </Button>
        <Button variant="ghost" onClick={stop(() => onSelect("feedback"))}>
          Give feedback
        </Button>
        <Button variant="ghost" onClick={stop(() => onSelect("goals"))}>
          Set {terms.goal.asTitle()}
        </Button>
      </Footer>
    </Card>
  );
};

/**
 * Has the same sections as a confirmed card (with the area rows greyed out)
 * so it comes out the same height and the grid lines up.
 */
const UnconfirmedCard = ({ user }: { user: DirectReportDto }) => {
  const terms = useGoalTerminology();
  const delRel = useDeleteRelationship();
  const nudge = useNudge(user.relationship.id);
  const hasMeasures =
    useProductVisibility("measures") === ProductVisibility.Enabled;

  const first = firstName(user.name);
  const waitingOnThem =
    user.relationship.fromAccepted != null &&
    user.relationship.toAccepted == null;
  const invited = formatDate(new Date(user.relationship.created));
  const pendingText = waitingOnThem
    ? `Available once ${first} accepts`
    : "Available once you accept";

  const del = () => {
    if (window.confirm("Are you sure?")) {
      delRel.mutateAsync(user.relationship.id);
    }
  };

  return (
    <Card pending>
      <CardHeader>
        <UserProfileImage
          profileImage={user.profileImage}
          name={user.name}
          size={48}
        />
        <NameBlock>
          <Name>{user.name}</Name>
          <Subtitle>{user.jobTitle ?? user.email}</Subtitle>
        </NameBlock>
        <MenuSpacer />
      </CardHeader>

      <CardStrip tone="warning">
        <StripText>
          <StripTag tone="warning">Pending</StripTag>
          <StripTitle>
            {waitingOnThem
              ? `Invited ${invited}. ${first} hasn't accepted yet.`
              : `Invited ${invited}. You haven't accepted yet.`}
          </StripTitle>
        </StripText>
        {nudge.visible && (
          <span title={nudge.title}>
            <Button
              variant="primary"
              disabled={!nudge.canNudge}
              onClick={nudge.send}
            >
              {nudge.label}
            </Button>
          </span>
        )}
      </CardStrip>

      <Rows aria-label={pendingText}>
        <AreaRow
          icon={FaBullseye}
          title={terms.goal.asPluralTitle()}
          detail={pendingText}
          muted
        />
        <AreaRow
          icon={FaCommentDots}
          title="Conversations"
          detail={pendingText}
          muted
        />
        <AreaRow icon={FaEnvelope} title="Feedback" detail={pendingText} muted />
        {hasMeasures && (
          <AreaRow
            icon={FaRegChartBar}
            title="Measures"
            detail={pendingText}
            muted
          />
        )}
      </Rows>

      <Footer>
        <Button variant="ghost" color="danger" onClick={del}>
          {waitingOnThem ? "Withdraw invitation" : "Remove"}
        </Button>
      </Footer>
    </Card>
  );
};
